import { CreditBalanceSummary } from '@/types/billing.types';
import { formatPrice, CREDIT_ACTIONS } from '../../../config/billingConfig';
import { Zap, Calendar, TrendingUp, Settings } from 'lucide-react';

interface CurrentPlanBannerProps {
  summary: CreditBalanceSummary;
  onManageBilling?: () => void;
  isPortalLoading: boolean;
}

export function CurrentPlanBanner({
  summary,
  onManageBilling,
  isPortalLoading,
}: CurrentPlanBannerProps) {
  const balance = summary.balance;
  const cap = summary.plan === 'freemium' ? 105 : summary.plan === 'team' ? ((summary.credits_per_cycle / 2) || 1) : (summary.credits_per_cycle || 1);
  const usedCredits = Math.max(0, cap - balance);
  const usedPercent = Math.min(100, Math.round((usedCredits / cap) * 100));

  const formatPeriodEnd = (iso: string | null) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div
      style={{
        background: 'var(--admin-card-bg)',
        border: '1px solid var(--admin-card-border)',
        borderRadius: '0',
        padding: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '20px',
      }}
    >
      {/* Left: plan info */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Zap size={18} color="var(--color-status-sky, #38bdf8)" />
          <span
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: '20px',
              color: 'var(--admin-card-text)',
              textTransform: 'capitalize',
            }}
          >
            {summary.plan} Plan
          </span>
          {summary.billing_cycle && (
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--color-status-sky, #38bdf8)',
                background: 'rgba(56,189,248,0.1)',
                borderRadius: '6px',
                padding: '2px 8px',
                textTransform: 'capitalize',
              }}
            >
              {summary.billing_cycle}
            </span>
          )}
        </div>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: 'var(--admin-card-subtext)', margin: 0 }}>
          {summary.credits_per_cycle > 0
            ? summary.plan === 'team'
              ? `${(summary.credits_per_cycle / 2).toLocaleString()} credits per user / cycle`
              : `${summary.credits_per_cycle.toLocaleString()} credits / cycle`
            : 'No recurring credits on this plan'}
        </p>
      </div>

      {/* Center: credit usage bar */}
      <div style={{ flex: 1, minWidth: '200px', maxWidth: '360px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '6px',
          }}
        >
          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: 'var(--admin-card-subtext)' }}>
            Credits remaining
          </span>
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: '13px',
              fontWeight: 700,
              color: 'var(--admin-card-text)',
            }}
          >
            {balance.toLocaleString()}
          </span>
        </div>
        <div
          style={{
            height: '6px',
            borderRadius: '99px',
            background: 'var(--admin-card-border)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${100 - usedPercent}%`,
              borderRadius: '99px',
              background: 'var(--color-status-sky, #38bdf8)',
              transition: 'width 0.5s ease',
            }}
          />
        </div>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '11px', color: 'var(--admin-card-subtext)', marginTop: '4px' }}>
          {usedPercent}% used this cycle
        </p>
      </div>

      {/* Right: renewal + manage */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
        {summary.plan !== 'freemium' && summary.current_period_end && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Calendar size={13} color="var(--admin-card-subtext)" />
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: 'var(--admin-card-subtext)' }}>
              {summary.cancel_at_period_end
                ? `Cancels ${formatPeriodEnd(summary.current_period_end)}`
                : `Renews ${formatPeriodEnd(summary.current_period_end)}`}
            </span>
          </div>
        )}
        {onManageBilling && (
          <button
            onClick={onManageBilling}
            disabled={isPortalLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: '0',
              backgroundColor: '#ffffff',
              border: 'none',
              color: '#000000',
              fontSize: '13px',
              fontFamily: "'Inter', sans-serif",
              fontWeight: 700,
              cursor: isPortalLoading ? 'not-allowed' : 'pointer',
              opacity: isPortalLoading ? 0.6 : 1,
              transition: 'all 0.2s',
            }}
          >
            <Settings size={14} />
            {isPortalLoading ? 'Loading…' : 'Manage Billing'}
          </button>
        )}
      </div>
    </div>
  );
}
